import type { SVGProps } from "react";

/**
 * The icon set, hand drawn.
 *
 * A dozen 24px outline glyphs is not worth an icon package: the whole set below costs less than the
 * import statement would, it ships nothing unused, and the stroke weight is tuned once here to sit
 * with the rest of the type rather than shouting over it.
 *
 * All of them inherit `currentColor` and are marked `aria-hidden`, because every place they appear
 * already has a text label next to them.
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Glyph({ size = 24, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </Glyph>
  );
}

export function PlusCircleIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8.5v7M8.5 12h7" />
    </Glyph>
  );
}

export function MinusCircleIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12h7" />
    </Glyph>
  );
}

/** Freeze a package: the days stop until it ends. */
export function PauseCircleIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 9v6M14 9v6" />
    </Glyph>
  );
}

export function CardIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="2.5" y="5" width="19" height="14" rx="3" />
      <path d="M2.5 10h19" />
    </Glyph>
  );
}

/** A bank note: money paid in cash or by transfer (Record a payment). */
export function MoneyIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="2.5" y="6" width="19" height="12" rx="2.5" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M6 9.5v5M18 9.5v5" />
    </Glyph>
  );
}

export function PersonIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
    </Glyph>
  );
}

export function PeopleIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="9.5" cy="8.5" r="3.2" />
      <path d="M3.2 19.5a6.3 6.3 0 0 1 12.6 0" />
      <path d="M16.4 6.1a3.2 3.2 0 0 1 0 6.1M17.6 14.4a6.3 6.3 0 0 1 3.2 5.1" />
    </Glyph>
  );
}

export function TagIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M11.6 3.4 20 11.8a2 2 0 0 1 0 2.9l-5.3 5.3a2 2 0 0 1-2.9 0L3.4 11.6V3.4Z" />
      <circle cx="7.6" cy="7.6" r="1.3" />
    </Glyph>
  );
}

export function MoreIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="5.5" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="18.5" cy="12" r="1.4" />
    </Glyph>
  );
}

export function SignOutIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M14.5 8.2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6.5a2 2 0 0 0 2-2v-2.2" />
      <path d="M10 12h10m0 0-3-3m3 3-3 3" />
    </Glyph>
  );
}

export function AlertIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.6v5M12 16.1h.01" />
    </Glyph>
  );
}

export function CloseIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Glyph>
  );
}

export function CheckIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4.5 12.5 9.5 17.5 19.5 6.5" />
    </Glyph>
  );
}

export function ChevronLeftIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </Glyph>
  );
}

export function ChevronRightIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M9.5 5.5 16 12l-6.5 6.5" />
    </Glyph>
  );
}

export function CopyIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="9" y="9" width="11.5" height="11.5" rx="2.5" />
      <path d="M15 5.6A2.6 2.6 0 0 0 12.4 3H6a2.6 2.6 0 0 0-2.6 2.6V12A2.6 2.6 0 0 0 6 14.6" />
    </Glyph>
  );
}

export function RepeatIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 9.5A4.5 4.5 0 0 1 8.5 5H19m0 0-3-3m3 3-3 3" />
      <path d="M20 14.5a4.5 4.5 0 0 1-4.5 4.5H5m0 0 3 3m-3-3 3-3" />
    </Glyph>
  );
}

export function TicketIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M3.5 9V7a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6Z" />
      <path d="M13 6.5v11" />
    </Glyph>
  );
}

export function LockIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="4.5" y="10" width="15" height="10.5" rx="2.5" />
      <path d="M8.2 10V7.6a3.8 3.8 0 0 1 7.6 0V10" />
    </Glyph>
  );
}

export function SparkIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9Z" />
    </Glyph>
  );
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M20 12H4m0 0 6-6m-6 6 6 6" />
    </Glyph>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 12h16m0 0-6-6m6 6-6 6" />
    </Glyph>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5.2l3.4 2" />
    </Glyph>
  );
}

export function ShieldIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 3.2 19 6v5.6c0 4-2.8 7.2-7 9.2-4.2-2-7-5.2-7-9.2V6Z" />
      <path d="m9 12 2.1 2.1L15.4 10" />
    </Glyph>
  );
}

export function BuildingIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 20V6.5a2 2 0 0 1 1.4-1.9l7-2.2A1.4 1.4 0 0 1 14.2 3.7V20M4 20h16.5M14.2 9H19a1 1 0 0 1 1 1v10" />
      <path d="M8 9h2.5M8 13h2.5" />
    </Glyph>
  );
}

export function InfoIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5.2M12 7.9h.01" />
    </Glyph>
  );
}

export function TrashIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4.5 6.5h15M9.5 6.5V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v1.5" />
      <path d="M6.5 6.5 7.4 19a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4l.9-12.5" />
    </Glyph>
  );
}

export function ChatIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H10l-4.2 3.2c-.4.3-.8 0-.8-.4V16A2.5 2.5 0 0 1 4 13.5z" />
    </Glyph>
  );
}

export function MegaphoneIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 10v4a1 1 0 0 0 1 1h2l6 4V5L7 9H5a1 1 0 0 0-1 1z" />
      <path d="M17 9.5a3.5 3.5 0 0 1 0 5M9 15l1 5" />
    </Glyph>
  );
}

export function SendIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4.5 11.5 19 5l-4.5 15-3-6.5z" />
      <path d="M11.5 13.5 19 5" />
    </Glyph>
  );
}

/** The microphone of the AI Assistant composer: record a question. */
export function MicIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0" />
      <path d="M12 18v2.5" />
    </Glyph>
  );
}

/** Stop a recording. */
export function StopIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" stroke="none" />
    </Glyph>
  );
}

// --- the desktop shell ------------------------------------------------------

export function HomeIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h5v-6h4v6h5V10" />
    </Glyph>
  );
}

export function LayersIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 3l9 5-9 5-9-5 9-5z" />
      <path d="M3 13l9 5 9-5" />
    </Glyph>
  );
}

export function UploadIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M12 16V4M7 9l5-5 5 5" />
      <path d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
    </Glyph>
  );
}

export function SettingsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" />
    </Glyph>
  );
}

export function PulseIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M3 12h4l3-7 4 14 3-7h4" />
    </Glyph>
  );
}

export function MenuIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Glyph>
  );
}

export function ChevronDownIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M6 9l6 6 6-6" />
    </Glyph>
  );
}

export function SearchIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </Glyph>
  );
}

export function SwitchIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 7h13l-3-3M20 17H7l3 3" />
    </Glyph>
  );
}

/** A wrench: the Tools section of the owner office. */
export function ToolsIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M14.7 6.3a4 4 0 00-5.4 5.1L4 16.7a1.9 1.9 0 002.7 2.7l5.3-5.3a4 4 0 005.1-5.4l-2.5 2.5-2.4-.4-.4-2.4z" />
    </Glyph>
  );
}

/** A camera: profile photos and the Team photo tool. */
export function CameraIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V9a1 1 0 011-1z" />
      <circle cx="12" cy="13" r="3.5" />
    </Glyph>
  );
}

// --- support ----------------------------------------------------------------

/** Help and support: a ring buoy. */
export function HelpIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
      <path d="M5.6 5.6l3.6 3.6M14.8 14.8l3.6 3.6M18.4 5.6l-3.6 3.6M9.2 14.8l-3.6 3.6" />
    </Glyph>
  );
}

/** A file on a message: a paper clip. */
export function PaperclipIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M20 11.5l-7.8 7.8a5 5 0 0 1-7.1-7.1l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7l-8.5 8.5a1.7 1.7 0 0 1-2.4-2.4l7.8-7.8" />
    </Glyph>
  );
}

/** "It helped": a thumb up. */
export function ThumbUpIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3z" />
      <path d="M7 10l4-7a2.2 2.2 0 0 1 2.9 2.6L13 10h5.6a2 2 0 0 1 2 2.4l-1.4 7A2 2 0 0 1 17.2 21H7" />
    </Glyph>
  );
}

/** "It did not help": a thumb down. */
export function ThumbDownIcon(props: IconProps) {
  return (
    <Glyph {...props}>
      <path d="M17 14V3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3z" />
      <path d="M17 14l-4 7a2.2 2.2 0 0 1-2.9-2.6L11 14H5.4a2 2 0 0 1-2-2.4l1.4-7A2 2 0 0 1 6.8 3H17" />
    </Glyph>
  );
}
